import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Client, CreateClientDto, UpdateClientDto } from 'api';
import { clientService } from 'api';
import { MoreHorizontal, Pencil } from 'lucide-react';
import { useSnackbar } from 'notistack';
import React, { useMemo, useState } from 'react';
import { ConfirmDialog } from 'shared/components/ConfirmDialog';
import type { DataTableColumn } from 'shared/components/DataTable';
import { DataTable, TableScrollBox } from 'shared/components/DataTable';
import { HeroContainer } from 'shared/components/HeroContainer';
import { queryKeys } from 'shared/constants/queryKeys';
import { usePermissions } from 'shared/hooks/usePermissions';

import {
  ActionCell,
  ApplyButton,
  ClearFiltersLink,
  CLIENTS_MENU_ITEM_SX,
  CLIENTS_MENU_PAPER_SX,
  CLIENTS_MENU_TEXT_SX,
  Container,
  DeleteMenuItem,
  FilterSelect,
  FilterToolbarCard,
  HeroAddButton,
  HeroBannerButtonBox,
  HeroBannerRow,
  HeroBannerTextBox,
  HeroSubtitle,
  HeroTitle,
  SearchFieldBox,
  SearchIconWrap,
  SearchInput,
  STATUS_MENU_ITEM_SX,
  STATUS_MENU_LIST_SX,
  STATUS_MENU_PAPER_SX,
} from '../clients.styles';

import { ClientFormModal } from './ClientFormModal';

type StatusFilter = 'active' | 'archived' | 'all';

interface RowMenuState {
  anchorEl: HTMLElement | null;
  clientId: string | null;
}

interface DeleteDialogState {
  open: boolean;
  clientId: string | null;
}

interface ArchiveDialogState {
  open: boolean;
  clientId: string | null;
  isArchiving: boolean;
}

interface ClientModalState {
  open: boolean;
  clientId: string | null;
  initialName: string;
}

const ClientsPage: React.FC = () => {
  const { canCreateClient, canEditClient, canDeleteClient } = usePermissions();
  const [appliedStatusFilter, setAppliedStatusFilter] = useState<StatusFilter>('active');
  const [pendingStatusFilter, setPendingStatusFilter] = useState<StatusFilter>('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  // const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [rowMenu, setRowMenu] = useState<RowMenuState>({ anchorEl: null, clientId: null });
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({
    open: false,
    clientId: null,
  });
  const [archiveDialog, setArchiveDialog] = useState<ArchiveDialogState>({
    open: false,
    clientId: null,
    isArchiving: false,
  });
  const [clientModal, setClientModal] = useState<ClientModalState>({
    open: false,
    clientId: null,
    initialName: '',
  });

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { data: clients = [], isLoading } = useQuery({
    queryKey: queryKeys.clients.list(),
    queryFn: () => clientService.getClients().then((res) => res.data.data),
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateClientDto) => clientService.createClient(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all });
      handleCloseModal();
      enqueueSnackbar('Client created', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Failed to create client', { variant: 'error' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateClientDto }) =>
      clientService.updateClient(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all });
      handleCloseModal();
      enqueueSnackbar('Client updated', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Failed to update client', { variant: 'error' });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateClientDto }) =>
      clientService.updateClient(id, data),
    onSuccess: (_res, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all });
      enqueueSnackbar(variables.data.isArchived ? 'Client archived' : 'Client unarchived', {
        variant: 'success',
      });
    },
    onError: () => {
      enqueueSnackbar('Failed to update client', { variant: 'error' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => clientService.deleteClient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clients.all });
      enqueueSnackbar('Client deleted', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Failed to delete client', { variant: 'error' });
    },
  });

  const filteredClients = useMemo(() => {
    return clients?.filter((client) => {
      const matchesStatus =
        appliedStatusFilter === 'all' ||
        (appliedStatusFilter === 'active' && !client.isArchived) ||
        (appliedStatusFilter === 'archived' && client.isArchived);
      const matchesSearch =
        !appliedSearch || client.name.toLowerCase().includes(appliedSearch.toLowerCase());
      return matchesStatus && matchesSearch;
    });
  }, [clients, appliedStatusFilter, appliedSearch]);

  const openRowMenu = (event: React.MouseEvent<HTMLElement>, clientId: string) => {
    setRowMenu({ anchorEl: event.currentTarget, clientId });
  };

  const closeRowMenu = () => {
    setRowMenu({ anchorEl: null, clientId: null });
  };

  const handleEditClick = (clientId: string) => {
    setClientModal({ open: true, clientId, initialName: '' });
  };

  const handleCloseModal = () => {
    setClientModal({ open: false, clientId: null, initialName: '' });
  };

  const hasFilters = appliedStatusFilter !== 'active' || appliedSearch !== '';

  const handleClearFilters = () => {
    setPendingStatusFilter('active');
    setSearchTerm('');
    setAppliedStatusFilter('active');
    setAppliedSearch('');
  };

  const handleApplyFilters = () => {
    setAppliedStatusFilter(pendingStatusFilter);
    setAppliedSearch(searchTerm);
  };

  const handleModalSubmit = (data: CreateClientDto | UpdateClientDto) => {
    if (clientModal.clientId) {
      updateMutation.mutate({ id: clientModal.clientId, data: data as UpdateClientDto });
    } else {
      createMutation.mutate(data as CreateClientDto);
    }
  };

  const handleArchiveToggle = () => {
    if (!rowMenu.clientId) return;
    const client = clients?.find((c) => c.id === rowMenu.clientId);
    if (!client) return;

    // Only show confirmation dialog when archiving (not unarchiving)
    if (!client.isArchived) {
      setArchiveDialog({ open: true, clientId: rowMenu.clientId, isArchiving: true });
      closeRowMenu();
    } else {
      // Directly unarchive without confirmation
      archiveMutation.mutate({
        id: rowMenu.clientId,
        data: {
          name: client.name,
          email: client.email,
          address: client.address,
          note: client.note,
          currency: client.currency,
          isArchived: false,
        },
      });
      closeRowMenu();
    }
  };

  const handleArchiveConfirm = () => {
    if (!archiveDialog.clientId) return;
    const client = clients?.find((c) => c.id === archiveDialog.clientId);
    if (!client) return;
    archiveMutation.mutate(
      {
        id: archiveDialog.clientId,
        data: {
          name: client.name,
          email: client.email,
          address: client.address,
          note: client.note,
          currency: client.currency,
          isArchived: true,
        },
      },
      {
        onSettled: () => setArchiveDialog({ open: false, clientId: null, isArchiving: false }),
      },
    );
  };

  const handleArchiveCancel = () => {
    setArchiveDialog({ open: false, clientId: null, isArchiving: false });
  };

  const handleDeleteClick = () => {
    setDeleteDialog({ open: true, clientId: rowMenu.clientId });
    closeRowMenu();
  };

  const handleDeleteConfirm = () => {
    if (!deleteDialog.clientId) return;
    deleteMutation.mutate(deleteDialog.clientId, {
      onSettled: () => setDeleteDialog({ open: false, clientId: null }),
    });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, clientId: null });
  };

  const columns: DataTableColumn<Client>[] = [
    {
      id: 'name',
      label: 'NAME',
      render: (client) => <span style={{ fontWeight: 600, fontSize: 13 }}>{client.name}</span>,
    },
    {
      id: 'mappedMemberCount',
      label: 'MAPPED MEMBERS',
      width: 200,
      render: (client) => client.mappedMemberCount,
    },
    {
      id: 'activeMappedMemberCount',
      label: 'ACTIVE MAPPED MEMBERS',
      width: 200,
      render: (client) => client.activeMappedMemberCount,
    },
    {
      id: 'activeContributorCount',
      label: 'ACTIVE CONTRIBUTORS',
      width: 200,
      render: (client) => client.activeContributorCount,
    },
    {
      id: 'inactiveMemberCount',
      label: 'INACTIVE CONTRIBUTORS',
      width: 200,
      render: (client) => client.inactiveMemberCount,
    },
    {
      id: 'actions',
      label: '',
      width: 100,
      align: 'right',
      render: (client) => (
        <ActionCell>
          {canEditClient && (
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => handleEditClick(client.id)}>
                <Pencil size={14} />
              </IconButton>
            </Tooltip>
          )}
          {(canEditClient || canDeleteClient) && (
            <IconButton size="small" onClick={(e) => openRowMenu(e, client.id)}>
              <MoreHorizontal size={14} />
            </IconButton>
          )}
        </ActionCell>
      ),
    },
  ];

  return (
    <Container>
      {/* Hero banner */}
      <HeroContainer padding="24px 28px" style={{ marginBottom: 20 }}>
        <HeroBannerRow>
          <HeroBannerTextBox>
            <HeroTitle>Clients</HeroTitle>
            <HeroSubtitle>Manage clients and their mapped members</HeroSubtitle>
          </HeroBannerTextBox>
          {canCreateClient && (
            <HeroBannerButtonBox>
              <HeroAddButton
                onClick={() => setClientModal({ open: true, clientId: null, initialName: '' })}
              >
                + New client
              </HeroAddButton>
            </HeroBannerButtonBox>
          )}
        </HeroBannerRow>
      </HeroContainer>

      {/* Filter toolbar */}
      <FilterToolbarCard>
        <SearchFieldBox>
          <SearchIconWrap>
            <SearchIcon sx={{ fontSize: 16 }} />
          </SearchIconWrap>
          <SearchInput
            placeholder="Search by name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchFieldBox>

        <FilterSelect
          value={pendingStatusFilter}
          onChange={(e) => setPendingStatusFilter(e.target.value as StatusFilter)}
          IconComponent={KeyboardArrowDownIcon}
          MenuProps={{
            PaperProps: { sx: STATUS_MENU_PAPER_SX },
            MenuListProps: { sx: STATUS_MENU_LIST_SX },
          }}
        >
          {['active', 'archived', 'all'].map((val) => (
            <MenuItem key={val} value={val} sx={STATUS_MENU_ITEM_SX}>
              {val.charAt(0).toUpperCase() + val.slice(1)}
            </MenuItem>
          ))}
        </FilterSelect>

        {hasFilters && (
          <ClearFiltersLink type="button" onClick={handleClearFilters}>
            Clear filters
          </ClearFiltersLink>
        )}

        <ApplyButton variant="contained" disableElevation onClick={handleApplyFilters}>
          Apply
        </ApplyButton>
      </FilterToolbarCard>

      <TableScrollBox>
        <DataTable<Client>
          columns={columns}
          rows={filteredClients}
          getRowId={(client) => client.id}
          emptyMessage={isLoading ? 'Loading clients…' : 'No clients match the current filter.'}
          borderless
          headerColor="#5A5578"
          headerWeight={500}
          headerFontSize="11px"
          rowHoverColor="rgba(110, 79, 247, 0.03)"
        />
      </TableScrollBox>

      {/* Row kebab menu */}
      <Menu
        anchorEl={rowMenu.anchorEl}
        open={Boolean(rowMenu.anchorEl)}
        onClose={closeRowMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: CLIENTS_MENU_PAPER_SX } }}
      >
        {canEditClient && (
          <MenuItem
            sx={CLIENTS_MENU_ITEM_SX}
            onClick={handleArchiveToggle}
            disabled={archiveMutation.isPending}
          >
            <ListItemText
              slotProps={{
                primary: {
                  sx: CLIENTS_MENU_TEXT_SX,
                },
              }}
            >
              {clients.find((c) => c.id === rowMenu.clientId)?.isArchived ? 'Unarchive' : 'Archive'}
            </ListItemText>
          </MenuItem>
        )}
        {canDeleteClient && (
          <DeleteMenuItem sx={CLIENTS_MENU_ITEM_SX} onClick={handleDeleteClick}>
            <ListItemText slotProps={{ primary: { sx: CLIENTS_MENU_TEXT_SX } }}>
              Delete
            </ListItemText>
          </DeleteMenuItem>
        )}
      </Menu>

      <ConfirmDialog
        open={deleteDialog.open}
        title="Delete Client"
        message="Are you sure you want to delete this client? This action cannot be undone."
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        loading={deleteMutation.isPending}
      />

      <ConfirmDialog
        open={archiveDialog.open}
        title="Archive Client"
        message="Archiving this client will also archive all projects under it. Are you sure you want to proceed?"
        confirmLabel="Archive"
        onConfirm={handleArchiveConfirm}
        onCancel={handleArchiveCancel}
        loading={archiveMutation.isPending}
      />

      <ClientFormModal
        open={clientModal.open}
        client={
          clientModal.clientId ? (clients.find((c) => c.id === clientModal.clientId) ?? null) : null
        }
        initialName={clientModal.initialName}
        onClose={handleCloseModal}
        onSubmit={handleModalSubmit}
        isPending={createMutation.isPending || updateMutation.isPending}
      />
    </Container>
  );
};

export default ClientsPage;
